import React from 'react';
import { Editor, EditorChangeEvent, EditorTools } from '@progress/kendo-react-editor';

const { Bold, Italic, Underline, FormatBlock, OrderedList, UnorderedList, Link, Unlink } = EditorTools;

interface AboutPageRichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
}

export const AboutPageRichTextEditor: React.FC<AboutPageRichTextEditorProps> = ({ value, onChange }) => {
  const editorRef = React.useRef<Editor>(null);

  React.useEffect(() => {
    const editorElement = editorRef.current?.element;
    if (!editorElement) {
      return undefined;
    }

    const iframe = editorElement.querySelector('iframe.k-iframe') as HTMLIFrameElement;
    if (!iframe || !iframe.contentDocument) {
      return undefined;
    }

    const iframeDoc = iframe.contentDocument;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        event.preventDefault();

        const selection = iframeDoc.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);

          const tabNode = iframeDoc.createTextNode('\u00a0\u00a0\u00a0\u00a0');
          range.deleteContents();
          range.insertNode(tabNode);

          range.setStartAfter(tabNode);
          range.setEndAfter(tabNode);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
    };

    iframeDoc.addEventListener('keydown', handleKeyDown);

    return () => {
      iframeDoc.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleChange = (event: EditorChangeEvent) => {
    onChange(event.html || '');
  };

  return (
    <Editor
      ref={editorRef}
      tools={[[Bold, Italic, Underline], FormatBlock, [OrderedList, UnorderedList], [Link, Unlink]]}
      contentStyle={{ height: 300 }}
      onChange={handleChange}
      value={value}
    />
  );
};
